/**
 * Pure admin helpers of the booth offering model (docs/plan-standangebot-modell.md, option O2, phases 2 + 3):
 * form state <-> JSON of the match rule (Product.boothMatchRule) and the price rule (Product.boothPriceMode /
 * Product.boothPriceRule), the preview "matches n booths of the active plan" with example prices of the booth
 * editor (src/pages-dev-admin/booths/EditBooth.jsx), the rows of "Create offer from features" and the
 * assignment matrix of the hall plan tab (src/pages-dev-admin/event-management/edit-event/HallPlanTab.jsx).
 *
 * Validation, matching and prices are NOT implemented here: they come from src/utils/boothMap.js
 * (validateBoothMatchRule, validateBoothPriceRule, matchAreasToProducts, ruleOffersFor, priceForBooth), the same
 * functions as in the boothBooking Lambda and placeOrder. Free of React, i18next and Amplify
 * (tested in scripts/booth-map/boothOfferAdmin.test.js).
 */
import {
  POSITION_TYPES,
  boothMatchRuleResult,
  cleanBoothNumber,
  compareBoothNumbers,
  groupAreasByFeatures,
  groupAreasByVariant,
  matchAreasToProducts,
  normalizeBoothNumber,
  normalizeVariant,
  priceForBooth,
  ruleOffersFor,
  validateBoothMatchRule,
  validateBoothPriceRule,
} from './boothMap';

/** Standard position keys (row / corner / head / island); free keys of the plan are valid too. */
export const STANDARD_POSITION_KEYS = POSITION_TYPES;

/** Column key of the assignment matrix for all single-booth products together. */
export const SINGLE_BOOTHS_COLUMN = '__single__';
/** Column key of the assignment matrix for booths without an offer. */
export const NO_OFFER_COLUMN = '__none__';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** Form number: '' / null -> null, '12,5' -> 12.5, anything else not numeric -> NaN. */
export function parseFormNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const text = String(value).trim().replace(',', '.');
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : NaN;
}

const numberToField = (value) => (value === null || value === undefined ? '' : String(value));

/** Trimmed keys (inner whitespace collapsed) without empty ones and case-insensitive duplicates; spelling kept. */
export function cleanKeyList(list) {
  const out = [];
  const seen = new Set();
  (Array.isArray(list) ? list : []).forEach((entry) => {
    const text = String(entry ?? '').trim().replace(/\s+/g, ' ');
    if (!text || seen.has(text.toLowerCase())) return;
    seen.add(text.toLowerCase());
    out.push(text);
  });
  return out;
}

/** A standard position key in any case becomes the standard key ('Corner' -> 'corner'); free keys are kept. */
export function normalizePositionKey(value) {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!text) return '';
  return POSITION_TYPES.find((key) => key === text.toLowerCase()) || text;
}

/** Raw AWSJSON / object -> parsed object or null (never throws). */
function parseRuleObject(raw) {
  let value = raw;
  for (let i = 0; i < 2 && typeof value === 'string'; i += 1) {
    if (!value.trim()) return null;
    try {
      value = JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

/** true when the raw rule value (AWSJSON string / object) is set at all (also when invalid). */
export function hasRuleValue(raw) {
  if (raw === null || raw === undefined) return false;
  if (typeof raw === 'string') return raw.trim() !== '' && raw.trim() !== 'null';
  return true;
}

/** { min, max, count } of the finite numbers of a list ({ min: null, max: null, count: 0 } when none). */
export function numberRange(values) {
  const list = (Array.isArray(values) ? values : []).filter((v) => typeof v === 'number' && Number.isFinite(v));
  if (list.length === 0) return { min: null, max: null, count: 0 };
  return { min: Math.min(...list), max: Math.max(...list), count: list.length };
}

// ---------------------------------------------------------------------------
// Match rule <-> form
// ---------------------------------------------------------------------------

/** Empty form state of the match rule editor (enabled false = no rule, only explicit codes). */
export const EMPTY_MATCH_RULE_FORM = Object.freeze({
  enabled: false,
  sizeMin: '',
  sizeMax: '',
  positionTypes: [],
  zones: [],
  priority: '',
});

/**
 * Stored match rule (Product.boothMatchRule, AWSJSON string or object) -> editor state
 * { enabled, sizeMin, sizeMax, positionTypes, zones, priority, invalid: string[] | null }.
 * An invalid stored rule (it never matches) is shown enabled with what could be read and its errors in `invalid`.
 */
export function matchRuleToForm(raw) {
  if (!hasRuleValue(raw)) return { ...EMPTY_MATCH_RULE_FORM, invalid: null };
  const { ok, rule, errors } = validateBoothMatchRule(raw);
  if (ok && rule) {
    return {
      enabled: true,
      sizeMin: numberToField(rule.sizeMin),
      sizeMax: numberToField(rule.sizeMax),
      positionTypes: rule.positionTypes.map(normalizePositionKey),
      zones: [...rule.zones],
      priority: rule.priority ? String(rule.priority) : '',
      invalid: null,
    };
  }
  if (ok) return { ...EMPTY_MATCH_RULE_FORM, invalid: null };
  const obj = parseRuleObject(raw) || {};
  const finite = (v) => (typeof v === 'number' && Number.isFinite(v) ? String(v) : '');
  return {
    enabled: true,
    sizeMin: finite(obj.sizeMin),
    sizeMax: finite(obj.sizeMax),
    positionTypes: cleanKeyList(Array.isArray(obj.positionTypes) ? obj.positionTypes.filter((v) => typeof v === 'string') : [])
      .map(normalizePositionKey),
    zones: cleanKeyList(Array.isArray(obj.zones) ? obj.zones.filter((v) => typeof v === 'string') : []),
    priority: finite(obj.priority),
    invalid: errors,
  };
}

/**
 * Editor state -> { rule: normalized rule | null, json: AWSJSON string | null, errors: string[], matchesAll }.
 * Disabled -> no rule (json null). Empty fields / lists are left out (no condition); the object is validated with
 * boothMap.validateBoothMatchRule (errors: plain English messages). matchesAll = enabled rule without any condition.
 */
export function matchRuleFromForm(form) {
  if (!form || !form.enabled) return { rule: null, json: null, errors: [], matchesAll: false };
  const errors = [];
  const out = {};
  [['sizeMin', 'Size from'], ['sizeMax', 'Size to'], ['priority', 'Priority']].forEach(([key, label]) => {
    const n = parseFormNumber(form[key]);
    if (Number.isNaN(n)) errors.push(`${label} must be a number.`);
    else if (n !== null) out[key] = n;
  });
  const positionTypes = cleanKeyList(form.positionTypes).map(normalizePositionKey);
  const zones = cleanKeyList(form.zones);
  if (positionTypes.length > 0) out.positionTypes = positionTypes;
  if (zones.length > 0) out.zones = zones;
  if (errors.length > 0) return { rule: null, json: null, errors, matchesAll: false };
  const result = validateBoothMatchRule(out);
  if (!result.ok || !result.rule) return { rule: null, json: null, errors: result.errors, matchesAll: false };
  const matchesAll = out.sizeMin === undefined && out.sizeMax === undefined && !out.positionTypes && !out.zones;
  return { rule: result.rule, json: JSON.stringify(out), errors: [], matchesAll };
}

// ---------------------------------------------------------------------------
// Price rule <-> form
// ---------------------------------------------------------------------------

export const EMPTY_PRICE_RULE_FORM = Object.freeze({
  mode: 'fixed',
  surcharges: [],
  minSqm: '',
  minPrice: '',
  roundSqm: 'none',
});

/** New surcharge row of the editor. */
export const newSurchargeRow = () => ({
  on: 'positionType', key: '', type: 'percent', value: '', capSqm: '',
});

/**
 * Stored price model (Product.boothPriceMode + Product.boothPriceRule) -> editor state
 * { mode: 'fixed'|'perSqm', surcharges: [{ on, key, type, value, capSqm }] (fields as strings),
 *   minSqm, minPrice, roundSqm, invalid: string[] | null }.
 */
export function priceRuleToForm(priceMode, raw) {
  const mode = String(priceMode || '').trim() === 'perSqm' ? 'perSqm' : 'fixed';
  if (!hasRuleValue(raw)) return { ...EMPTY_PRICE_RULE_FORM, mode, invalid: null };
  const { ok, rule, errors } = validateBoothPriceRule(raw);
  const source = ok && rule ? rule : parseRuleObject(raw) || {};
  const finite = (v) => (typeof v === 'number' && Number.isFinite(v) ? String(v) : '');
  const surcharges = (Array.isArray(source.surcharges) ? source.surcharges : [])
    .filter((s) => s && typeof s === 'object')
    .map((s) => ({
      on: s.on === 'zone' ? 'zone' : 'positionType',
      key: typeof s.key === 'string' ? s.key.trim() : '',
      type: ['percent', 'absolute', 'rate'].includes(s.type) ? s.type : 'percent',
      value: finite(s.value),
      capSqm: finite(s.capSqm),
    }));
  return {
    mode,
    surcharges,
    minSqm: finite(source.minSqm),
    minPrice: finite(source.minPrice),
    roundSqm: ['none', 'ceil', 'floor', 'round'].includes(source.roundSqm) ? source.roundSqm : 'none',
    invalid: ok ? null : errors,
  };
}

/**
 * Editor state -> { priceMode: 'perSqm' | null (null = fixed, the default), rule: normalized | null,
 * json: AWSJSON string | null, errors: string[] }. A rule without surcharges, minSqm, minPrice and rounding is
 * stored as null. Validated with boothMap.validateBoothPriceRule. 'rate' surcharges only work per m²
 * (error for a fixed price).
 */
export function priceRuleFromForm(form) {
  const state = form || EMPTY_PRICE_RULE_FORM;
  const priceMode = state.mode === 'perSqm' ? 'perSqm' : null;
  const errors = [];
  const surcharges = [];
  (Array.isArray(state.surcharges) ? state.surcharges : []).forEach((row, index) => {
    const label = `Surcharge ${index + 1}`;
    const key = String(row?.key ?? '').trim().replace(/\s+/g, ' ');
    const value = parseFormNumber(row?.value);
    const capSqm = parseFormNumber(row?.capSqm);
    if (!key && (value === null || Number.isNaN(value))) return; // empty row: ignored
    if (!key) errors.push(`${label}: please enter a key.`);
    if (value === null || Number.isNaN(value)) errors.push(`${label}: please enter a value.`);
    if (Number.isNaN(capSqm) || (capSqm !== null && capSqm <= 0)) errors.push(`${label}: the cap must be a number of m² greater than 0.`);
    if (row?.type === 'rate' && priceMode !== 'perSqm') errors.push(`${label}: an own rate per m² needs the price mode "per m²".`);
    const entry = {
      on: row?.on === 'zone' ? 'zone' : 'positionType',
      key: row?.on === 'zone' ? key : normalizePositionKey(key),
      type: ['percent', 'absolute', 'rate'].includes(row?.type) ? row.type : 'percent',
      value,
    };
    if (capSqm !== null && !Number.isNaN(capSqm)) entry.capSqm = capSqm;
    surcharges.push(entry);
  });
  const out = {};
  if (surcharges.length > 0) out.surcharges = surcharges;
  [['minSqm', 'Minimum area'], ['minPrice', 'Minimum price']].forEach(([key, label]) => {
    const n = parseFormNumber(state[key]);
    if (Number.isNaN(n) || (n !== null && n < 0)) errors.push(`${label} must be a number of at least 0.`);
    else if (n !== null) out[key] = n;
  });
  if (state.roundSqm && state.roundSqm !== 'none') out.roundSqm = state.roundSqm;
  if (errors.length > 0) return { priceMode, rule: null, json: null, errors };
  if (Object.keys(out).length === 0) return { priceMode, rule: null, json: null, errors: [] };
  const result = validateBoothPriceRule(out);
  if (!result.ok) return { priceMode, rule: null, json: null, errors: result.errors };
  return { priceMode, rule: result.rule, json: JSON.stringify(out), errors: [] };
}

// ---------------------------------------------------------------------------
// Matching descriptors and preview (booth editor)
// ---------------------------------------------------------------------------

/**
 * EventProducts of an event (with product) -> matchAreasToProducts descriptors (same shape as
 * useEventBoothPlan.toMatchProducts: sortKey = EventProduct.createdAt, boothMatchRule raw).
 */
export function offerDescriptors(eventProducts) {
  return (Array.isArray(eventProducts) ? eventProducts : [])
    .filter((ep) => ep && ep.product)
    .map((ep) => ({
      eventProductId: ep.id,
      productId: ep.product.id,
      boothNumber: ep.product.boothNumber || null,
      boothVariant: ep.product.boothVariant || null,
      boothVariantAliases: ep.product.boothVariantAliases || null,
      boothMatchRule: ep.product.boothMatchRule ?? null,
      sortKey: ep.createdAt || null,
    }));
}

/** Named areas of a list (placeholders '#n' and empty numbers skipped, first area per normalized number). */
export function namedAreas(areas) {
  const seen = new Set();
  const out = [];
  (Array.isArray(areas) ? areas : []).forEach((area) => {
    if (!area) return;
    const number = cleanBoothNumber(area.boothNumber);
    if (!number || number.startsWith('#')) return;
    const key = normalizeBoothNumber(number);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(area);
  });
  return out;
}

const DRAFT_ID = '__draft__';
// a product that is not linked to the event yet is matched as the youngest offer (sorted last)
const DRAFT_SORT_KEY = '9999-12-31T23:59:59.999Z';

/**
 * Preview of an offer being edited against the live areas of an event (booth editor, phase 2 + 3).
 * draft: { productId?, eventProductId? (EventProduct of this product in the event, if linked), createdAt?,
 *   boothNumber, boothVariant, boothVariantAliases, boothMatchRule (raw JSON / object / null),
 *   product: { price, boothPriceMode, boothPriceRule, boothArea } (draft values for the price),
 *   eventProduct: { price, boothPriceRule, timeRangePrices } | null }.
 * eventProducts: the event's booth EventProducts with product (the draft's own EventProduct is replaced).
 * -> {
 *   ruleMatches: named areas whose features match the draft rule (any outcome) - count,
 *   primary: booth numbers the draft is the primary offer of, alternative: booth numbers where it is a further offer,
 *   toOthers: [{ boothNumber, eventProductId, via }] rule matches won by another offer (single booth, plan code or a
 *     rule with higher priority / specificity / an older link),
 *   ambiguous: ambiguous-offer-match warnings naming the draft,
 *   prices: { min, max, count, missing: booth numbers without a price, examples: [{ boothNumber, area, details }] }
 *     over the booths the draft is primary for (priceForBooth at nowIso),
 * }
 */
export function offerRulePreview({
  areas, eventProducts, draft, nowIso = new Date().toISOString(), maxExamples = 8,
}) {
  const named = namedAreas(areas);
  const draftEpId = draft?.eventProductId || DRAFT_ID;
  const others = offerDescriptors(eventProducts).filter((d) => d.eventProductId !== draftEpId
    && (!draft?.productId || d.productId !== draft.productId));
  const draftDescriptor = {
    eventProductId: draftEpId,
    productId: draft?.productId || DRAFT_ID,
    boothNumber: draft?.boothNumber || null,
    boothVariant: draft?.boothVariant || null,
    boothVariantAliases: draft?.boothVariantAliases || null,
    boothMatchRule: draft?.boothMatchRule ?? null,
    sortKey: draft?.eventProductId ? draft.createdAt || null : DRAFT_SORT_KEY,
  };
  const result = matchAreasToProducts(named, [...others, draftDescriptor]);
  const ruleMatches = [];
  const primary = [];
  const alternative = [];
  const toOthers = [];
  named.forEach((area) => {
    const key = normalizeBoothNumber(area.boothNumber);
    const number = cleanBoothNumber(area.boothNumber);
    const offers = result.offers instanceof Map ? result.offers.get(key) || [] : [];
    const match = result.matches.get(key) || null;
    const index = offers.findIndex((o) => o && o.eventProductId === draftEpId);
    if (match && match.eventProductId === draftEpId) primary.push(number);
    else if (index > 0) alternative.push(number);
    if (boothMatchRuleResult(draftDescriptor.boothMatchRule, area)) {
      ruleMatches.push(number);
      if (match && match.eventProductId !== draftEpId) toOthers.push({ boothNumber: number, eventProductId: match.eventProductId, via: match.via });
    }
  });
  const ambiguous = result.warnings.filter((w) => w && w.code === 'ambiguous-offer-match'
    && Array.isArray(w.eventProductIds) && w.eventProductIds.includes(draftEpId));

  const byNumber = new Map(named.map((area) => [normalizeBoothNumber(area.boothNumber), area]));
  const totals = [];
  const missing = [];
  const examples = [];
  primary.forEach((number) => {
    const area = byNumber.get(normalizeBoothNumber(number));
    const details = priceForBooth({
      product: draft?.product || null, eventProduct: draft?.eventProduct || null, area, nowIso,
    });
    if (!details) {
      missing.push(number);
      return;
    }
    totals.push(details.total);
    if (examples.length < maxExamples) examples.push({ boothNumber: number, area, details });
  });
  // the examples show the spread: cheapest and most expensive booths first
  const range = numberRange(totals);
  const sortedExamples = examples.sort((a, b) => a.details.total - b.details.total);
  const sort = (list) => list.sort(compareBoothNumbers);
  return {
    total: named.length,
    ruleMatches: sort(ruleMatches),
    primary: sort(primary),
    alternative: sort(alternative),
    toOthers: toOthers.sort((a, b) => compareBoothNumbers(a.boothNumber, b.boothNumber)),
    ambiguous,
    prices: { ...range, missing: sort(missing), examples: sortedExamples },
  };
}

/** Distinct position types and zones of areas (trimmed, first spelling kept), for suggestion chips. */
export function areaFeatureKeys(areas) {
  return {
    positionTypes: cleanKeyList(namedAreas(areas).map((a) => normalizePositionKey(a.positionType))),
    zones: cleanKeyList(namedAreas(areas).map((a) => a.zone)),
  };
}

// ---------------------------------------------------------------------------
// "Create offer from features"
// ---------------------------------------------------------------------------

/** Short text of a match rule's conditions with the raw keys, e.g. '12 m² · corner · V' ('' = no condition). */
export function featureRuleLabel(rule) {
  if (!rule) return '';
  const size = (() => {
    const { sizeMin = null, sizeMax = null } = rule;
    if (sizeMin === null && sizeMax === null) return '';
    if (sizeMin !== null && sizeMax !== null) return sizeMin === sizeMax ? `${sizeMin} m²` : `${sizeMin}-${sizeMax} m²`;
    return sizeMin !== null ? `>= ${sizeMin} m²` : `<= ${sizeMax} m²`;
  })();
  return [size, (rule.positionTypes || []).join('/'), (rule.zones || []).join('/')].filter(Boolean).join(' · ');
}

/**
 * Rows of the create form for feature combinations (groupAreasByFeatures / featureCombinations entries
 * { key, size, positionType, zone, count, boothNumbers }): one offer with a match rule per combination
 * (size from = size to = the size, position type, zone; priority 0) instead of an offer code.
 * Combinations without any feature are skipped (their rule would match every booth).
 * -> [{ key: 'r:<combo key>', rule, label, nameCode, skuCode, count, area, boothNumbers }]
 */
export function featureRuleRows(combos) {
  const rows = [];
  (Array.isArray(combos) ? combos : []).forEach((combo) => {
    if (!combo) return;
    const sizeNumber = parseFormNumber(combo.size);
    const size = sizeNumber !== null && !Number.isNaN(sizeNumber) && sizeNumber > 0 ? sizeNumber : null;
    const positionType = normalizePositionKey(combo.positionType);
    const zone = String(combo.zone ?? '').trim().replace(/\s+/g, ' ');
    if (size === null && !positionType && !zone) return;
    const rule = {
      sizeMin: size,
      sizeMax: size,
      positionTypes: positionType ? [positionType] : [],
      zones: zone ? [zone] : [],
      priority: 0,
    };
    rows.push({
      key: `r:${String(combo.key ?? featureRuleLabel(rule)).toLowerCase()}`,
      rule,
      label: featureRuleLabel(rule),
      // used in the product name pattern ({{variant}}) and the SKU ('CP-ST-' + letters / digits / dots)
      nameCode: [positionType, zone].filter(Boolean).join(' '),
      skuCode: [size, positionType, zone].filter((v) => v !== null && v !== '').join('-'),
      count: Number(combo.count) || 0,
      area: size,
      boothNumbers: Array.isArray(combo.boothNumbers) ? combo.boothNumbers : [],
    });
  });
  return rows;
}

// ---------------------------------------------------------------------------
// Assignment matrix (hall plan tab)
// ---------------------------------------------------------------------------

const isOfferEventProduct = (ep) => {
  const product = ep && ep.product;
  if (!product || cleanBoothNumber(product.boothNumber)) return false;
  return Boolean(normalizeVariant(product.boothVariant)) || validateBoothMatchRule(product.boothMatchRule).rule !== null;
};

/**
 * Assignment matrix of the live areas of an event: which offer sells which booths.
 * rowsBy 'features': one row per size × position type × zone (boothMap.groupAreasByFeatures);
 * rowsBy 'codes': one row per plan code (groupAreasByVariant) plus one row for the areas without a code.
 * Columns: every offer of the event (offer code and / or match rule, in the order of eventProducts), one column for
 * all single-booth products together (SINGLE_BOOTHS_COLUMN) and one for booths without an offer (NO_OFFER_COLUMN),
 * the latter two only when used.
 * Cell: { primary, alternative, min, max } - booths whose primary offer is the column / where the column is a further
 * offer, and the price range of the primary booths (priceForBooth at nowIso, EventProduct + product of the column).
 * exceptions: single booths taking an area (kind 'single') and areas whose plan code wins over a matching rule of
 * another offer (kind 'code', ruleEventProductId = the rule offer that would get it).
 * warnings: the matching warnings (matchAreasToProducts); offersWithoutBooths: offer columns without any booth.
 */
export function assignmentMatrix(areas, eventProducts, { rowsBy = 'features', nowIso = new Date().toISOString() } = {}) {
  const named = namedAreas(areas);
  const descriptors = offerDescriptors(eventProducts);
  const result = matchAreasToProducts(named, descriptors);
  const epById = new Map((Array.isArray(eventProducts) ? eventProducts : []).filter((ep) => ep && ep.id).map((ep) => [ep.id, ep]));
  const singleIds = new Set(descriptors.filter((d) => normalizeBoothNumber(d.boothNumber)).map((d) => d.eventProductId));
  const columnOf = (eventProductId) => (singleIds.has(eventProductId) ? SINGLE_BOOTHS_COLUMN : eventProductId);
  const areaByKey = new Map(named.map((area) => [normalizeBoothNumber(area.boothNumber), area]));

  let groups;
  if (rowsBy === 'codes') {
    const coded = groupAreasByVariant(named).map((group) => ({
      key: `c:${group.variant}`, variant: group.variant, size: group.size, count: group.count, boothNumbers: group.boothNumbers,
    }));
    const uncoded = named.filter((area) => !normalizeVariant(area.variant)).map((area) => cleanBoothNumber(area.boothNumber));
    groups = uncoded.length > 0
      ? [...coded, {
        key: 'c:', variant: null, size: null, count: uncoded.length, boothNumbers: uncoded.sort(compareBoothNumbers),
      }]
      : coded;
  } else {
    groups = groupAreasByFeatures(named).map((group) => ({ ...group, key: `f:${group.key.toLowerCase()}` }));
  }

  const columnTotals = new Map();
  const bump = (column, field) => {
    const entry = columnTotals.get(column) || { primary: 0, alternative: 0 };
    entry[field] += 1;
    columnTotals.set(column, entry);
  };
  const rows = groups.map((group) => {
    const cells = new Map();
    const cellOf = (column) => {
      if (!cells.has(column)) cells.set(column, { primary: 0, alternative: 0, prices: [] });
      return cells.get(column);
    };
    group.boothNumbers.forEach((number) => {
      const key = normalizeBoothNumber(number);
      const offers = result.offers instanceof Map ? result.offers.get(key) || [] : [];
      const list = offers.length > 0 ? offers : (result.matches.get(key) ? [result.matches.get(key)] : []);
      if (list.length === 0) {
        cellOf(NO_OFFER_COLUMN).primary += 1;
        bump(NO_OFFER_COLUMN, 'primary');
        return;
      }
      const seenColumns = new Set();
      list.forEach((offer, index) => {
        const column = columnOf(offer.eventProductId);
        if (seenColumns.has(column)) return;
        seenColumns.add(column);
        const cell = cellOf(column);
        if (index === 0) {
          cell.primary += 1;
          bump(column, 'primary');
          const ep = epById.get(offer.eventProductId);
          const details = ep ? priceForBooth({
            product: ep.product, eventProduct: ep, area: areaByKey.get(key), nowIso,
          }) : null;
          if (details) cell.prices.push(details.total);
        } else {
          cell.alternative += 1;
          bump(column, 'alternative');
        }
      });
    });
    const finalCells = new Map();
    cells.forEach((cell, column) => {
      const { min, max } = numberRange(cell.prices);
      finalCells.set(column, {
        primary: cell.primary, alternative: cell.alternative, min, max,
      });
    });
    return { ...group, cells: finalCells };
  });

  const offerColumns = (Array.isArray(eventProducts) ? eventProducts : [])
    .filter(isOfferEventProduct)
    .map((ep) => ({ key: ep.id, eventProductId: ep.id, kind: 'offer' }));
  const columns = [...offerColumns];
  if (columnTotals.has(SINGLE_BOOTHS_COLUMN)) columns.push({ key: SINGLE_BOOTHS_COLUMN, eventProductId: null, kind: 'single' });
  if (columnTotals.has(NO_OFFER_COLUMN)) columns.push({ key: NO_OFFER_COLUMN, eventProductId: null, kind: 'none' });
  const withTotals = columns.map((column) => ({ ...column, ...(columnTotals.get(column.key) || { primary: 0, alternative: 0 }) }));

  const exceptions = [];
  named.forEach((area) => {
    const key = normalizeBoothNumber(area.boothNumber);
    const match = result.matches.get(key);
    if (!match) return;
    const number = cleanBoothNumber(area.boothNumber);
    if (match.via === 'number') {
      exceptions.push({
        kind: 'single', boothNumber: number, eventProductId: match.eventProductId, variant: normalizeVariant(area.variant) || null,
      });
      return;
    }
    if (match.via === 'variant' || match.via === 'code') {
      const ruleOffer = ruleOffersFor(area, descriptors).find((d) => d.eventProductId !== match.eventProductId);
      if (ruleOffer) {
        exceptions.push({
          kind: 'code',
          boothNumber: number,
          eventProductId: match.eventProductId,
          variant: normalizeVariant(area.variant) || null,
          ruleEventProductId: ruleOffer.eventProductId,
        });
      }
    }
  });
  exceptions.sort((a, b) => compareBoothNumbers(a.boothNumber, b.boothNumber));

  return {
    rows,
    columns: withTotals,
    exceptions,
    warnings: result.warnings,
    offersWithoutBooths: withTotals.filter((c) => c.kind === 'offer' && c.primary === 0 && c.alternative === 0).map((c) => c.eventProductId),
    areaCount: named.length,
  };
}

// ---------------------------------------------------------------------------
// Template copy of "Create offers" (E2E review 2, N3 / N4): texts and quantities follow the row
// ---------------------------------------------------------------------------

// letters / digits that continue a word (a code must not be part of a longer word: '6A' is not in '16A')
const WORD_CHAR = /[A-Za-z0-9ÄÖÜäöüß]/;
const DIGIT = /\d/;
const escapeRegExp = (text) => String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// "6 m²", "6m²", "6&nbsp;m²", "6 m&sup2;", "6 m2", "6 qm", "6 sqm", "6 sq m", "6,5 m²"
const SIZE_SOURCE = '(?<num>\\d+(?:[.,]\\d+)?)(?<space>(?:\\s|&nbsp;|\\u00a0)*)(?<unit>m²|m&sup2;|m2|qm|sqm|sq\\.?\\s?m)';

/** m² as a positive number ('12,5' accepted), else null. */
export function positiveSize(value) {
  const n = parseFormNumber(value);
  return n !== null && Number.isFinite(n) && n > 0 ? n : null;
}

const sameSize = (a, b) => Math.abs(a - b) < 1e-9;

/** m² as text: integers without decimals, else up to 2 decimals with the given separator. */
const formatSize = (value, separator) => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', separator);
};

/**
 * Size change between a template and the row it is copied to: { from, to } when both m² are known and
 * differ, else null (no warning, no scaling).
 */
export function templateSizeChange(templateSize, rowSize) {
  const from = positiveSize(templateSize);
  const to = positiveSize(rowSize);
  return from !== null && to !== null && !sameSize(from, to) ? { from, to } : null;
}

/**
 * Text of a template product retargeted to the row it is copied to ("Create offers", template per row):
 * - whole-word occurrences of the template's offer code (case-insensitive), e.g. 'Variante 6A', 'variant 6a',
 *   '(6A)', become the row's code; '16A', '6AB' or '6A' inside '6,5' stay;
 * - size expressions with the template's m² ('6 m²', '6m²', 'ca. 6 m²', '6 sqm', '6 qm', '6&nbsp;m&sup2;')
 *   get the row's m² (only the number changes, unit and spacing are kept); a size expression is never treated
 *   as a code, so a numeric code equal to the size ('17' with 17 m²) does not double-replace.
 * HTML tags (and their attributes) are left untouched, only the text between them changes. Without a row code
 * (rule offers) or with equal sizes that part is skipped. The decimal separator of a new non-integer m² follows
 * the replaced number ('6,5' -> ','), else `decimalSeparator` (default ',').
 *
 * @param {string|null|undefined} text
 * @param {{ fromCode?: string|null, fromSize?: number|string|null, toCode?: string|null, toSize?: number|string|null,
 *   decimalSeparator?: string }} values
 * @returns {string|null|undefined} the retargeted text (non-strings are returned unchanged)
 */
export function retargetTemplateText(text, {
  fromCode = null, fromSize = null, toCode = null, toSize = null, decimalSeparator = ',',
} = {}) {
  if (typeof text !== 'string' || !text) return text;
  const codeFrom = String(fromCode ?? '').trim();
  const codeTo = String(toCode ?? '').trim();
  const replaceCode = Boolean(codeFrom && codeTo && codeFrom.toLowerCase() !== codeTo.toLowerCase());
  const sizeChange = templateSizeChange(fromSize, toSize);
  if (!replaceCode && !sizeChange) return text;
  // size first: a size expression is consumed as a whole and never seen as a code
  const source = replaceCode ? `${SIZE_SOURCE}|(?<code>${escapeRegExp(codeFrom)})` : SIZE_SOURCE;
  const pattern = new RegExp(source, 'gi');
  const retargetSegment = (segment) => segment.replace(pattern, (...args) => {
    const match = args[0];
    const groups = args[args.length - 1];
    const string = args[args.length - 2];
    const offset = args[args.length - 3];
    const before = offset > 0 ? string[offset - 1] : '';
    const before2 = offset > 1 ? string[offset - 2] : '';
    const after = string[offset + match.length] || '';
    const after2 = string[offset + match.length + 1] || '';
    // part of a longer word / number ('16A', '6AB', 'x6 m²', '1,6 m²'): unchanged
    if (WORD_CHAR.test(before) || ((before === '.' || before === ',') && DIGIT.test(before2))) return match;
    if (groups.code !== undefined) {
      if (WORD_CHAR.test(after) || ((after === '.' || after === ',') && DIGIT.test(after2))) return match;
      return codeTo;
    }
    if (WORD_CHAR.test(after) || !sizeChange) return match;
    const number = Number(String(groups.num).replace(',', '.'));
    if (!sameSize(number, sizeChange.from)) return match;
    let separator = decimalSeparator;
    if (groups.num.includes(',')) separator = ',';
    else if (groups.num.includes('.')) separator = '.';
    return `${formatSize(sizeChange.to, separator)}${groups.space}${groups.unit}`;
  });
  // only the text between HTML tags (odd parts of the split are the tags)
  return text.split(/(<[^>]*>)/).map((part, index) => (index % 2 === 1 ? part : retargetSegment(part))).join('');
}

/**
 * retargetTemplateText values of a template product and a row of "Create offers": template code = its offer code
 * (boothVariant), else its booth number; row code = the row's offer code, else its booth number ('' for rule
 * offers: their code part is skipped); sizes = template boothArea and row m².
 *
 * @param {{ boothVariant?: string, boothNumber?: string, boothArea?: number }|null} template
 * @param {{ variant?: string, boothNumber?: string, area?: number|string|null }} row
 */
export function templateRetargetValues(template, { variant = '', boothNumber = '', area = null } = {}) {
  return {
    fromCode: String(template?.boothVariant || template?.boothNumber || '').trim(),
    fromSize: template?.boothArea ?? null,
    toCode: String(variant || boothNumber || '').trim(),
    toSize: area,
  };
}

/**
 * retargetTemplateText for a MultiLang list ([{ language, text }]): German texts use ',' as decimal separator,
 * all others '.'. Returns a new list (entries without text are kept as they are); non-arrays are returned unchanged.
 */
export function retargetMultiLang(list, values) {
  if (!Array.isArray(list)) return list;
  return list.map((entry) => {
    if (!entry || typeof entry.text !== 'string') return entry;
    return {
      ...entry,
      text: retargetTemplateText(entry.text, { ...values, decimalSeparator: entry.language === 'de' ? ',' : '.' }),
    };
  });
}

/**
 * Product names of an offer copied from a template without a name pattern: the template's German / English
 * name retargeted to the row (code and m²); a missing English name is derived from the German one. Without
 * any template name the row's default names are kept (`fallback`, buildBoothProductNames with an empty pattern).
 *
 * @param {Array<{language: string, text: string}>|null} templateName
 * @param {object} values retargetTemplateText values (fromCode, fromSize, toCode, toSize)
 * @param {{ de: string, en: string }} fallback
 * @returns {{ de: string, en: string, fromTemplate: boolean }}
 */
export function retargetTemplateNames(templateName, values, fallback) {
  const text = (language) => {
    const entry = (Array.isArray(templateName) ? templateName : []).find((item) => item && item.language === language);
    return String(entry?.text || '').trim();
  };
  const de = text('de');
  // like buildBoothProductNames: without an English name a leading "Ausstellungsstand" becomes "Exhibition stand"
  const en = text('en') || de.replace(/^Ausstellungsstand/, 'Exhibition stand');
  return {
    de: de ? retargetTemplateText(de, { ...values, decimalSeparator: ',' }) : fallback.de,
    en: en ? retargetTemplateText(en, { ...values, decimalSeparator: '.' }) : fallback.en,
    fromTemplate: Boolean(de || en),
  };
}

/**
 * Component quantities of a template copied to a row with another m² (N4): a quantity equal to the template's
 * m² (e.g. carpet 9 for a 9 m² template, also the rounded-up m² of a non-integer size) becomes the row's m²
 * (rounded up, ProductComponent.qty is an Int); every other quantity stays. Without a size change
 * (templateSizeChange) the components are returned unchanged.
 *
 * @param {Array<{componentProductID: string, qty: number}>} components
 * @returns {{ components: object[], scaled: Array<{componentProductID: string, from: number, to: number}>,
 *   sizeChange: {from: number, to: number}|null }}
 */
export function scaleTemplateComponents(components, { fromSize = null, toSize = null } = {}) {
  const list = (Array.isArray(components) ? components : []).filter(Boolean);
  const sizeChange = templateSizeChange(fromSize, toSize);
  if (!sizeChange) return { components: list, scaled: [], sizeChange: null };
  const target = Math.ceil(sizeChange.to - 1e-9);
  const scaled = [];
  const out = list.map((component) => {
    const qty = Number(component.qty);
    if (Number.isFinite(qty) && (sameSize(qty, sizeChange.from) || qty === Math.ceil(sizeChange.from - 1e-9)) && qty !== target) {
      scaled.push({ componentProductID: component.componentProductID, from: qty, to: target });
      return { ...component, qty: target };
    }
    return component;
  });
  return { components: out, scaled, sizeChange };
}
